// js/timesheet.js — Day/Week/Month/Year rendering and inline cell editing

import * as Cal  from './calendar.js';
import * as Data from './data.js';
import { buildNavBar } from './nav.js';
import { isMobile, renderMobileTimesheet } from './mobile.js';

const container = () => document.getElementById('view-timesheet');

// ── Undo state ────────────────────────────────────────────────────────────────

let _undoAction = null;
let _undoTimer  = null;

// ── Data cache — populated once per render ────────────────────────────────────
let _projects   = [];
let _categories = [];

async function refreshCache() {
  [_projects, _categories] = await Promise.all([
    Data.getProjects(),
    Data.getCategories(),
  ]);
}

export async function performUndo() {
  if (!_undoAction) return;
  const action = _undoAction;
  _undoAction = null;
  hideUndoToast();
  if (action.type === 'delete') {
    await Data.restoreEntry(action.entry);
  } else {
    await Data.saveEntry(action.entry);
  }
  await renderTimesheet();
}

function showUndoToast(message) {
  hideUndoToast();
  const toast = document.createElement('div');
  toast.id = 'undo-toast';
  toast.className = 'undo-toast';
  toast.innerHTML = `
    <span class="undo-toast-msg">${message}</span>
    <button class="undo-toast-undo">Undo</button>
    <button class="undo-toast-dismiss">✕</button>
  `;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('undo-toast-show'));
  toast.querySelector('.undo-toast-undo').addEventListener('click', performUndo);
  toast.querySelector('.undo-toast-dismiss').addEventListener('click', () => {
    _undoAction = null;
    hideUndoToast();
  });
  _undoTimer = setTimeout(() => { _undoAction = null; hideUndoToast(); }, 5000);
}

function hideUndoToast() {
  if (_undoTimer) { clearTimeout(_undoTimer); _undoTimer = null; }
  document.getElementById('undo-toast')?.remove();
}

// ── Public entry point ────────────────────────────────────────────────────────

export async function renderTimesheet() {
  await refreshCache();

  // Remove any mobile FAB that may be lingering from a previous render
  document.getElementById('mob-fab')?.remove();

  // Delegate to mobile renderer on small screens
  if (isMobile()) {
    await renderMobileTimesheet(_projects, _categories);
    return;
  }

  const c = container();
  c.innerHTML = '';

  const navWrap = document.createElement('div');
  buildNavBar(navWrap, renderTimesheet);
  c.appendChild(navWrap);

  switch (Cal.getView()) {
    case 'day':   await renderDayView(c);   break;
    case 'week':  await renderWeekView(c);  break;
    case 'month': await renderMonthView(c); break;
    case 'year':  await renderYearView(c);  break;
  }
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function formatDuration(h) {
  if (!h) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm ? `${hh}h ${mm}m` : `${hh}h`;
}

function projectName(id) {
  if (!id) return '—';
  return (_projects.find(p => p.id === id) || {}).name || '—';
}

function categoryName(id) {
  return (_categories.find(c => c.id === id) || {}).name || '—';
}

function categoryColor(id) {
  return (_categories.find(c => c.id === id) || {}).color || '#94a3b8';
}

function categoryType(id) {
  return (_categories.find(c => c.id === id) || {}).type || 'work';
}

/** Total hours for an array of entries */
function totalHours(entries) {
  return entries.reduce((s, e) => s + (e.duration || 0), 0);
}

// ── Add Entry button ──────────────────────────────────────────────────────────

function addEntryBtn(dateISO) {
  const btn = document.createElement('button');
  btn.className = 'mt-3 px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700';
  btn.id = 'btn-add-entry';
  btn.textContent = '+ Add Entry';
  btn.addEventListener('click', async () => {
    const _now = new Date();
    const _sh = _now.getHours();
    const _sm = Math.floor(_now.getMinutes() / 15) * 15;
    const _startTime = `${String(_sh).padStart(2,'0')}:${String(_sm).padStart(2,'0')}`;
    const _endTime   = `${String((_sh + 1) % 24).padStart(2,'0')}:${String(_sm).padStart(2,'0')}`;
    const entry = await Data.saveEntry({
      date: dateISO,
      projectId:  _projects[0]?.id || null,
      categoryId: _categories[0]?.id || '',
      startTime:  _startTime,
      endTime:    _endTime,
      description: '',
    });
    await renderTimesheet();
    setTimeout(() => {
      const row = document.querySelector(`tr[data-id="${entry.id}"]`);
      if (row) row.querySelector('.cell-editable')?.click();
    }, 0);
  });
  return btn;
}

// ── Table builder ─────────────────────────────────────────────────────────────

// Column definitions: [header label, fixed width or null for flexible]
const COLS_NO_DATE = [
  ['Category',    '178px'],
  ['Project',     '201px'],
  ['Start',        '95px'],
  ['End',          '95px'],
  ['Duration',     '82px'],
  ['Description',   null ],   // flexible — takes remaining space
  ['',             '44px'],   // delete button
];
const COLS_WITH_DATE = [
  ['Date',        '115px'],
  ...COLS_NO_DATE,
];

function buildEntriesTable(entries, showDate = false) {
  const table = document.createElement('table');
  table.className = 'w-full text-sm border-collapse';
  table.style.tableLayout = 'fixed';

  const colDefs = showDate ? COLS_WITH_DATE : COLS_NO_DATE;

  // <colgroup> locks each column to its specified width
  const colgroup = document.createElement('colgroup');
  colDefs.forEach(([, w]) => {
    const col = document.createElement('col');
    if (w) col.style.width = w;
    colgroup.appendChild(col);
  });
  table.appendChild(colgroup);

  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
      ${colDefs.map(([h]) => `<th class="py-2 px-3 font-medium">${h}</th>`).join('')}
    </tr>`;
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  tbody.id = 'entry-tbody';
  entries.forEach(entry => tbody.appendChild(buildEntryRow(entry, showDate)));
  table.appendChild(tbody);
  return table;
}

// ── Entry row + inline editing ────────────────────────────────────────────────

function buildEntryRow(entry, showDate = false) {
  const tr = document.createElement('tr');
  tr.dataset.id = entry.id;
  tr.dataset.date = entry.date;
  tr.className = 'border-b border-gray-100 hover:bg-gray-50 group';

  const startTd = makeTimeCell(entry, 'startTime');
  const endTd   = makeTimeCell(entry, 'endTime');

  const cells = showDate
    ? [makeDateCell(entry), makeCategoryCell(entry), makeProjectCell(entry), startTd, endTd, makeDurationCell(entry), makeDescriptionCell(entry), makeDeleteCell(entry)]
    : [makeCategoryCell(entry), makeProjectCell(entry), startTd, endTd, makeDurationCell(entry), makeDescriptionCell(entry), makeDeleteCell(entry)];

  cells.forEach(td => tr.appendChild(td));

  if (entry.startTime && entry.endTime && entry.startTime >= entry.endTime) {
    startTd.classList.add('time-order-error');
    endTd.classList.add('time-order-error');
  }

  return tr;
}

// ── Cell factories ────────────────────────────────────────────────────────────

function makeDateCell(entry) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3 text-gray-500 whitespace-nowrap';
  const d = Cal.fromISODate(entry.date);
  td.textContent = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return td;
}

function makeCategoryCell(entry) {
  return makeSelectCell(entry, 'categoryId', () =>
    _categories.map(c => ({ value: c.id, label: c.name, color: c.color }))
  , (td, val) => {
    td.innerHTML = `<span class="inline-flex items-center gap-1.5">
      <span class="color-swatch" style="background:${categoryColor(val)}"></span>
      ${categoryName(val)}
    </span>`;
  });
}

function makeProjectCell(entry) {
  return makeSelectCell(entry, 'projectId', () => {
    const opts = _projects.filter(p => p.active).map(p => ({ value: p.id, label: p.name }));
    if (categoryType(entry.categoryId) !== 'work') opts.unshift({ value: '', label: '—' });
    return opts;
  }, (td, val) => { td.textContent = projectName(val); });
}

function makeTimeCell(entry, field) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3 cell-editable whitespace-nowrap';
  td.dataset.field = field;
  td.textContent = entry[field] || '—';
  td.addEventListener('click', () => activateTimeEdit(td, entry, field));
  return td;
}

function makeDescriptionCell(entry) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3 cell-editable w-full';
  td.textContent = entry.description || '';
  td.addEventListener('click', () => activateTextEdit(td, entry, 'description'));
  return td;
}

function makeDurationCell(entry) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3 text-gray-500 whitespace-nowrap';
  td.dataset.durationFor = entry.id;
  td.textContent = formatDuration(entry.duration);
  return td;
}

function makeDeleteCell(entry) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3';
  const btn = document.createElement('button');
  btn.className = 'text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs px-2 py-1 rounded hover:bg-red-50';
  btn.textContent = '✕';
  btn.title = 'Delete entry';
  btn.addEventListener('click', async () => {
    if (confirm('Delete this entry?')) {
      _undoAction = { type: 'delete', entry: { ...entry } };
      await Data.deleteEntry(entry.id);
      showUndoToast('Entry deleted');
      await renderTimesheet();
    }
  });
  td.appendChild(btn);
  return td;
}

// ── Custom portal dropdown ────────────────────────────────────────────────────
// Appended to <body> so it escapes any overflow:hidden ancestors.
// Opens synchronously on the first click — no native <select> two-click issue.

function openDropdown(anchorTd, opts, currentValue, onSelect) {
  closeDropdown(); // ensure only one is open at a time

  const rect = anchorTd.getBoundingClientRect();
  const panel = document.createElement('div');
  panel.id = 'tl-dropdown';
  panel.className = 'tl-dropdown';
  panel.style.cssText =
    `position:fixed;left:${rect.left}px;top:${rect.bottom + 2}px;` +
    `min-width:${Math.max(rect.width, 160)}px;`;

  let focusIdx = opts.findIndex(o => o.value === (currentValue || ''));
  if (focusIdx < 0) focusIdx = 0;

  const items = opts.map((o, i) => {
    const item = document.createElement('div');
    item.className = 'tl-dropdown-item' +
      (o.value === (currentValue || '') ? ' is-selected' : '');

    if (o.color) {
      const dot = document.createElement('span');
      dot.className = 'tl-dropdown-dot';
      dot.style.background = o.color;
      item.appendChild(dot);
    }
    const label = document.createElement('span');
    label.textContent = o.label;
    item.appendChild(label);

    item.addEventListener('mousedown', e => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(o.value);
      closeDropdown();
    });
    panel.appendChild(item);
    return item;
  });

  const highlight = () => {
    items.forEach((el, i) => el.classList.toggle('is-focused', i === focusIdx));
    items[focusIdx]?.scrollIntoView({ block: 'nearest' });
  };

  const onKey = e => {
    if (e.key === 'Escape')    { closeDropdown(); }
    if (e.key === 'ArrowDown') { focusIdx = Math.min(focusIdx + 1, items.length - 1); highlight(); e.preventDefault(); }
    if (e.key === 'ArrowUp')   { focusIdx = Math.max(focusIdx - 1, 0);                highlight(); e.preventDefault(); }
    if (e.key === 'Enter')     { onSelect(opts[focusIdx].value); closeDropdown(); }
  };
  document.addEventListener('keydown', onKey);

  document.body.appendChild(panel);
  highlight();

  // Close when clicking outside the panel and outside the anchor
  setTimeout(() => {
    const onOutside = e => {
      if (!panel.contains(e.target) && !anchorTd.contains(e.target)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', onOutside);
    panel._cleanup = () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, 0);
}

function closeDropdown() {
  const el = document.getElementById('tl-dropdown');
  if (el) { el._cleanup?.(); el.remove(); }
}

// ── Inline edit helpers ───────────────────────────────────────────────────────

function makeSelectCell(entry, field, optsFn, renderFn) {
  const td = document.createElement('td');
  td.className = 'py-2 px-3 cell-editable whitespace-nowrap';
  renderFn(td, entry[field]);

  td.addEventListener('click', () => {
    // If our dropdown is already open for this cell, close it (toggle)
    if (document.getElementById('tl-dropdown')) { closeDropdown(); return; }

    openDropdown(td, optsFn(), entry[field] || '', async value => {
      const prevEntry = { ...entry };
      const newVal = value || null;
      entry[field] = newVal;
      const saved = await Data.saveEntry(entry);
      Object.assign(entry, saved);
      renderFn(td, entry[field]);
      if ((prevEntry[field] || null) !== newVal) {
        _undoAction = { type: 'edit', entry: prevEntry };
        showUndoToast('Change saved');
      }
    });
  });

  return td;
}

// ── Time normaliser ───────────────────────────────────────────────────────────
// Accepts: "09:00" · "9:00" · "900" · "0900" · "9" → always returns "HH:MM"

function activateTimeEdit(td, entry, field) {
  if (td.querySelector('input')) return;
  const original = entry[field] || '';

  const input = document.createElement('input');
  input.className = 'cell-input';
  input.readOnly = true;
  input.style.cursor = 'pointer';
  input.value = original;

  td.innerHTML = '';
  td.appendChild(input);

  let escaped = false;
  let lastKnownVal = original;

  const fp = flatpickr(input, {
    enableTime:    true,
    noCalendar:    true,
    dateFormat:    'H:i',
    time_24hr:     true,
    defaultDate:      original || new Date(),
    minuteIncrement:  15,
    disableMobile:    true,

    onChange(selectedDates, dateStr) {
      if (dateStr) lastKnownVal = dateStr;
    },

    onClose: async function(selectedDates, dateStr) {
      requestAnimationFrame(() => fp.destroy());
      const val = escaped ? original : (dateStr || lastKnownVal);
      if (val && val !== original) {
        const prevEntry = { ...entry };
        entry[field] = val;
        const saved = await Data.saveEntry(entry);
        Object.assign(entry, saved);
        const durCell = document.querySelector(`[data-duration-for="${entry.id}"]`);
        if (durCell) durCell.textContent = formatDuration(saved.duration);
        await refreshOverlapWarnings(entry.date);
        _undoAction = { type: 'edit', entry: prevEntry };
        showUndoToast('Time updated');
      }
      td.textContent = entry[field] || '—';
      refreshTimeOrderWarning(entry);
    },
  });

  // Escape cancels without saving
  input.addEventListener('keydown', e => {
    if (e.key === 'Escape') { escaped = true; fp.close(); }
  });

  fp.open();
}

function activateTextEdit(td, entry, field) {
  if (td.querySelector('input')) return; // already editing
  const original = entry[field] || '';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'cell-input';
  input.value = original;

  td.innerHTML = '';
  td.appendChild(input);
  input.focus();

  let done = false;
  const commit = async () => {
    if (done) return;
    done = true;
    const newVal = input.value;
    if (newVal !== original) {
      const prevEntry = { ...entry };
      entry[field] = newVal;
      const saved = await Data.saveEntry(entry);
      Object.assign(entry, saved);
      td.textContent = newVal;
      _undoAction = { type: 'edit', entry: prevEntry };
      showUndoToast('Description updated');
    } else {
      td.textContent = original;
    }
  };
  const cancel = () => {
    if (done) return;
    done = true;
    td.textContent = original;
  };
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter')  { commit(); }
    if (e.key === 'Escape') { cancel(); }
  });
  input.addEventListener('blur', commit);
}


// ── Overlap detection ─────────────────────────────────────────────────────────

async function getOverlappingIds(dateISO) {
  const entries = (await Data.getEntries(dateISO, dateISO))
    .filter(e => e.startTime && e.endTime);
  const ids = new Set();
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i], b = entries[j];
      if (a.startTime < b.endTime && a.endTime > b.startTime) {
        ids.add(a.id);
        ids.add(b.id);
      }
    }
  }
  return ids;
}

function refreshTimeOrderWarning(entry) {
  const tr = document.querySelector(`tr[data-id="${entry.id}"]`);
  if (!tr) return;
  const invalid = !!(entry.startTime && entry.endTime && entry.startTime >= entry.endTime);
  tr.querySelector('[data-field="startTime"]')?.classList.toggle('time-order-error', invalid);
  tr.querySelector('[data-field="endTime"]')?.classList.toggle('time-order-error', invalid);
}

async function refreshOverlapWarnings(dateISO) {
  const ids = await getOverlappingIds(dateISO);
  document.querySelectorAll(`[data-overlap-warn="${dateISO}"]`).forEach(el => {
    el.classList.toggle('hidden', ids.size === 0);
  });
  document.querySelectorAll(`tr[data-id][data-date="${dateISO}"]`).forEach(tr => {
    tr.classList.toggle('entry-overlap', ids.has(tr.dataset.id));
  });
}

// ── Day view ──────────────────────────────────────────────────────────────────

async function renderDayView(c) {
  const { from } = Cal.getCurrentRange();
  const year = parseInt(from.slice(0, 4), 10);
  const [entries, holidays] = await Promise.all([
    Data.getEntries(from, from),
    Data.getAllHolidays(year),
  ]);
  const holidayName = holidays[from];

  const wrap = document.createElement('div');
  const total = totalHours(entries);
  wrap.innerHTML = `<p class="text-xs text-gray-400 mb-2">Total: <strong>${formatDuration(total)}</strong></p>`;
  if (holidayName) {
    const p = document.createElement('p');
    p.className = 'text-xs font-semibold mb-3';
    p.style.color = '#F3185A';
    p.textContent = holidayName;
    wrap.appendChild(p);
  }
  const overlapDiv = document.createElement('div');
  overlapDiv.dataset.overlapWarn = from;
  overlapDiv.className = 'hidden text-xs font-medium px-3 py-1.5 mb-2 rounded border';
  overlapDiv.style.cssText = 'color:#92400e;background:#fffbeb;border-color:#fcd34d;';
  overlapDiv.textContent = '⚠ Overlapping entries on this day — check start/end times.';
  wrap.appendChild(overlapDiv);
  if (entries.length) {
    wrap.appendChild(buildEntriesTable(entries, false));
  } else {
    const noEntries = document.createElement('p');
    noEntries.className = 'text-gray-400 text-sm';
    noEntries.textContent = 'No entries for this day.';
    wrap.appendChild(noEntries);
  }
  wrap.appendChild(addEntryBtn(from));
  c.appendChild(wrap);
  await refreshOverlapWarnings(from);
}

// ── Week view ─────────────────────────────────────────────────────────────────

async function renderWeekView(c) {
  const { from, to } = Cal.getCurrentRange();

  const yearFrom = parseInt(from.slice(0, 4), 10);
  const yearTo   = parseInt(to.slice(0, 4), 10);
  const [entries, holidaysFrom, holidaysTo] = await Promise.all([
    Data.getEntries(from, to),
    Data.getAllHolidays(yearFrom),
    yearTo !== yearFrom ? Data.getAllHolidays(yearTo) : Promise.resolve({}),
  ]);
  const holidays = { ...holidaysFrom, ...holidaysTo };

  // Group by date
  const byDate = {};
  entries.forEach(e => { (byDate[e.date] = byDate[e.date] || []).push(e); });

  // Generate all 7 days
  const days = [];
  const start = Cal.fromISODate(from);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(Cal.toISODate(d));
  }

  const wrap = document.createElement('div');
  const weekTotal = totalHours(entries);
  wrap.innerHTML = `<p class="text-xs text-gray-400 mb-3">Week total: <strong>${formatDuration(weekTotal)}</strong></p>`;

  const table = document.createElement('table');
  table.className = 'w-full text-sm border-collapse';
  table.style.tableLayout = 'fixed';

  // Week view uses the same 7-column layout as day view (no extra Date column —
  // the date is shown in the day-header rows that span the full width).
  const wColgroup = document.createElement('colgroup');
  COLS_NO_DATE.forEach(([, w]) => {
    const col = document.createElement('col');
    if (w) col.style.width = w;
    wColgroup.appendChild(col);
  });
  table.appendChild(wColgroup);

  table.innerHTML += `
    <thead>
      <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
        <th class="py-2 px-3">Category</th>
        <th class="py-2 px-3">Project</th>
        <th class="py-2 px-3">Start</th>
        <th class="py-2 px-3">End</th>
        <th class="py-2 px-3">Duration</th>
        <th class="py-2 px-3">Description</th>
        <th class="py-2 px-3"></th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody');

  days.forEach(dateISO => {
    const dayEntries = byDate[dateISO] || [];
    const d = Cal.fromISODate(dateISO);
    const dayLabel = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
    const dayTotal = totalHours(dayEntries);

    // Day header row — full colspan 7, Add button inline with the day label
    const holidayName = holidays[dateISO];
    const headerRow = document.createElement('tr');
    headerRow.className = `day-header-row border-b border-gray-300${holidayName ? ' is-holiday' : ''}`;
    headerRow.innerHTML = `
      <td colspan="7" class="py-2 px-3">
        <div class="flex items-center gap-3">
          <span class="text-gray-700 font-semibold">${dayLabel}</span>
          ${holidayName ? `<span class="text-xs font-semibold" style="color:#F3185A">${holidayName}</span>` : ''}
          <span class="text-xs font-normal text-gray-400">${formatDuration(dayTotal)}</span>
          <button class="add-day-btn text-xs font-medium text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50 whitespace-nowrap" data-date="${dateISO}">+ Add</button>
        </div>
      </td>
    `;
    tbody.appendChild(headerRow);

    const warnRow = document.createElement('tr');
    warnRow.className = 'hidden';
    warnRow.dataset.overlapWarn = dateISO;
    warnRow.innerHTML = `<td colspan="7" class="px-3 py-1 text-xs font-medium" style="color:#92400e;background:#fffbeb;">⚠ Overlapping entries on this day — check start/end times.</td>`;
    tbody.appendChild(warnRow);

    dayEntries.forEach(entry => tbody.appendChild(buildEntryRow(entry, false)));
  });

  // Wire add buttons
  table.querySelectorAll('.add-day-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const _now2 = new Date();
      const _sh2 = _now2.getHours();
      const _sm2 = Math.floor(_now2.getMinutes() / 15) * 15;
      const _startTime2 = `${String(_sh2).padStart(2,'0')}:${String(_sm2).padStart(2,'0')}`;
      const _endTime2   = `${String((_sh2 + 1) % 24).padStart(2,'0')}:${String(_sm2).padStart(2,'0')}`;
      const entry = await Data.saveEntry({
        date: btn.dataset.date,
        projectId:  _projects[0]?.id || null,
        categoryId: _categories[0]?.id || '',
        startTime: _startTime2,
        endTime: _endTime2,
        description: '',
      });
      await renderTimesheet();
      setTimeout(() => {
        const row = document.querySelector(`tr[data-id="${entry.id}"]`);
        if (row) row.querySelector('.cell-editable')?.click();
      }, 0);
    });
  });

  wrap.appendChild(table);
  c.appendChild(wrap);
  await Promise.all(days.map(dateISO => refreshOverlapWarnings(dateISO)));
}

// ── Month view ────────────────────────────────────────────────────────────────

async function renderMonthView(c) {
  const { from, to } = Cal.getCurrentRange();

  const d = Cal.getDate();
  const year  = d.getFullYear();
  const month = d.getMonth();

  const [entries, holidays] = await Promise.all([
    Data.getEntries(from, to),
    Data.getAllHolidays(year),
  ]);

  // Group entries by date
  const entriesByDay = {};
  entries.forEach(e => { (entriesByDay[e.date] = entriesByDay[e.date] || []).push(e); });

  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const todayISO = Cal.toISODate(new Date());

  let startOffset = firstDay.getDay() - 1; // 0 = Mon
  if (startOffset < 0) startOffset = 6;

  const grid = document.createElement('div');
  grid.className = 'select-none';

  // Day-of-week headers
  const header = document.createElement('div');
  header.className = 'grid grid-cols-7 mb-1';
  ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].forEach(day => {
    const h = document.createElement('div');
    h.className = 'text-center text-xs font-medium py-1';
    h.style.color = '#7889B3';
    h.textContent = day;
    header.appendChild(h);
  });
  grid.appendChild(header);

  const cellsWrap = document.createElement('div');
  cellsWrap.className = 'grid grid-cols-7 gap-1';

  // Blank cells before first day
  for (let i = 0; i < startOffset; i++) {
    const blank = document.createElement('div');
    blank.className = 'cal-cell rounded p-1 other-month';
    cellsWrap.appendChild(blank);
  }

  for (let day = 1; day <= lastDay.getDate(); day++) {
    const dateISO  = Cal.toISODate(new Date(year, month, day));
    const isHoliday = holidays.hasOwnProperty(dateISO);
    const dayEntries = entriesByDay[dateISO] || [];
    const hours = dayEntries.reduce((s, e) => s + (e.duration || 0), 0);

    const cell = document.createElement('div');
    cell.className = `cal-cell rounded p-1 ${dateISO === todayISO ? 'today' : ''} ${isHoliday ? 'holiday' : ''}`;

    // Header row: holiday name (left) + day number (right)
    const dayHead = document.createElement('div');
    dayHead.className = 'flex items-start justify-between gap-1';

    if (isHoliday) {
      const hName = document.createElement('span');
      hName.className = 'holiday-name leading-tight';
      hName.textContent = holidays[dateISO];
      dayHead.appendChild(hName);
    } else {
      dayHead.appendChild(document.createElement('span')); // spacer
    }

    const dayNum = document.createElement('span');
    dayNum.className = `day-num text-xs font-semibold ${isHoliday ? 'holiday-day-num' : ''}`;
    dayNum.textContent = day;
    dayHead.appendChild(dayNum);
    cell.appendChild(dayHead);

    // Entry pills (up to 3, then "+N more")
    if (dayEntries.length) {
      const entryList = document.createElement('div');
      entryList.className = 'mt-1 space-y-px';
      const visible = dayEntries.slice(0, 3);
      visible.forEach(entry => {
        const pill = document.createElement('div');
        pill.className = 'cal-entry-pill flex items-center gap-1';
        const dot = document.createElement('span');
        dot.className = 'cal-entry-dot flex-shrink-0';
        dot.style.background = categoryColor(entry.categoryId);
        const label = document.createElement('span');
        label.className = 'cal-entry-label truncate';
        const timeStr = entry.startTime && entry.startTime !== '00:00'
          ? `${entry.startTime} `
          : '';
        label.textContent = `${timeStr}${categoryName(entry.categoryId)}`;
        pill.appendChild(dot);
        pill.appendChild(label);
        entryList.appendChild(pill);
      });
      if (dayEntries.length > 3) {
        const more = document.createElement('div');
        more.className = 'cal-entry-more';
        more.textContent = `+${dayEntries.length - 3} more`;
        entryList.appendChild(more);
      }
      cell.appendChild(entryList);
    }

    // Total hours footer
    if (hours) {
      const footer = document.createElement('div');
      footer.className = 'cal-day-total';
      footer.textContent = formatDuration(hours);
      cell.appendChild(footer);
    }

    cell.addEventListener('click', async () => {
      Cal.setView('day');
      Cal.setDate(Cal.fromISODate(dateISO));
      await renderTimesheet();
    });
    cellsWrap.appendChild(cell);
  }

  grid.appendChild(cellsWrap);
  c.appendChild(grid);
}

// ── Year view ─────────────────────────────────────────────────────────────────

async function renderYearView(c) {
  const { from, to } = Cal.getCurrentRange();
  const entries = await Data.getEntries(from, to);

  const hoursByMonth = {};
  entries.forEach(e => {
    const m = e.date.slice(0, 7); // YYYY-MM
    hoursByMonth[m] = (hoursByMonth[m] || 0) + (e.duration || 0);
  });

  const year = Cal.getDate().getFullYear();
  const MONTH_NAMES = ['January','February','March','April','May','June',
                       'July','August','September','October','November','December'];

  const grid = document.createElement('div');
  grid.className = 'grid grid-cols-3 gap-4 sm:grid-cols-4';

  MONTH_NAMES.forEach((name, idx) => {
    const monthKey = `${year}-${String(idx + 1).padStart(2, '0')}`;
    const hours = hoursByMonth[monthKey] || 0;
    const cell = document.createElement('div');
    cell.className = 'cal-cell rounded-lg p-4 text-center cursor-pointer';
    cell.innerHTML = `
      <div class="font-semibold text-gray-700">${name}</div>
      ${hours ? `<div class="text-blue-600 mt-1 text-sm">${formatDuration(hours)}</div>` : '<div class="text-gray-300 mt-1 text-sm">—</div>'}
    `;
    cell.addEventListener('click', async () => {
      Cal.setView('month');
      Cal.setDate(new Date(year, idx, 1));
      await renderTimesheet();
    });
    grid.appendChild(cell);
  });

  c.appendChild(grid);
}
